// =================================================================================================
import { ReactNode, Component, PureComponent, Fragment, ReactElement } from 'react';
import _ from 'lodash';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { Odometer as PrimaryOdometer, OdometerTheme } from '@Primitive/Odometer';
import { WebThing, WebThingPort } from '@API/WebThing';
import { ReduxThingState, StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { LabelAutoSize } from '@Primitive/Typography';
import { getIconClassName } from '@Primitive/IconSelectorUtils';
import { SettingsList } from '@Primitive/SettingsList';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { LocalizationMap } from '@Primitive/Localization/Localization';
import { StateManager } from "@Utils/StateManager";
// =================================================================================================
interface OdometerData {
    Theme: OdometerTheme;
    AnimationCount: boolean;
    Format: string;
    BackgroundColor: string;
    TextColor: string;
    Title: string;
    TitleIcon: string;
    HideIcon: boolean;
}
// -------------------------------------------------------------------------------------------------
interface OdometerState {
    Value: number;
}
const enum PortKeys {
    Value = 'Value'
}
// -------------------------------------------------------------------------------------------------
class Odometer extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<OdometerData>, OdometerState> {
    // ---------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<OdometerData>) {
        super(props);
        this.state = { Value: 0 }
    }
    // ---------------------------------------------------------------------------------------------
    updatePortValue(portKey: string, portState: string) {
        try {
            if (portKey == PortKeys.Value &&
                isDefined(portState)) {
                let data: number = JSON.parse(portState);
                this.setState({ Value: data });
            }
        }
        catch (ex) { }
    }
    // ---------------------------------------------------------------------------------------------
    componentDidMount() {
        if (_.has(this.props.PortsStates, PortKeys.Value))
            this.updatePortValue(PortKeys.Value, this.props.PortsStates[PortKeys.Value].State);

        // Link update port
        let stateManager = StateManager.GetInstanceKey(this.props.NodeKey);
        if (isDefined(stateManager))
            stateManager.RegisterCB(this.props.ThingKey, this.stateUpdate);
    }
    // ---------------------------------------------------------------------------------------------------
    componentWillUnmount() {
        let stateManager = StateManager.GetInstanceKey(this.props.NodeKey);
        if (isDefined(stateManager))
            stateManager.UnRegisterCB(this.props.ThingKey, this.stateUpdate);
    }
    // ---------------------------------------------------------------------------------------------
    stateUpdate = (state: ReduxThingState, forceUpdate: boolean, changedSubKeys: string[]) => {
        for (const portKey of changedSubKeys) {
            let port = state.PortStates[portKey];
            if (port != null &&
                port.LastStateStatus == StateStatus.Valid)
                this.updatePortValue(portKey, port.State);
        }
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let title = this.props.LocalizationMap['Title'];
        if (IsNullOrEmpty(title)) {
            title = this.props.WidgetData.Title;
        }
        let itemWidth = this.props.width - 20;
        let hideIcon = this.props.WidgetData.HideIcon;

        return <div style={{
            width: this.props.width,
            height: this.props.height,
            backgroundColor: this.props.WidgetData.BackgroundColor,
            color: this.props.WidgetData.TextColor,
            padding: "10px 14px",
            textAlign: "center",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            display: "flex"
        }}>
            <LabelAutoSize
                Text={<Fragment>
                    <i style={(hideIcon) ? { display: "none" } : { paddingRight: 8 }} className={getIconClassName(this.props.WidgetData.TitleIcon)} >
                    </i>
                    {title}
                </Fragment>}
                Height={30}
                Width={itemWidth}
                MaxFontSize={20}
                Style={{ fontWeight: 500, margin: "0.2rem 0 0.2rem 0" }}
            />
            <div
                style={{
                    fontSize: "3rem",
                    margin: "0.2rem 0 0.2rem 0",
                    fontWeight: 500,
                    lineHeight: "3rem"
                }}
            >
                <PrimaryOdometer
                    animationCount={this.props.WidgetData.AnimationCount}
                    theme={this.props.WidgetData.Theme}
                    format={this.props.WidgetData.Format}
                    value={this.state.Value}
                    duration={2000}
                />
            </div>
        </div>
    }
}
// =================================================================================================
class OdometerSettings extends PureComponent<IWidgetSettingsProps<OdometerData>> {
    constructor(props: IWidgetSettingsProps<OdometerData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<OdometerData>[] = [
            {
                name: "Text",
                elements: [
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Background Color',
                        tooltip: "Sets the widget's background color.",
                        dataPath: "BackgroundColor",
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Text Color',
                        tooltip: "Sets the widget's text color.",
                        dataPath: "TextColor",
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Hide Icon',
                        tooltip: "If disabled an icon can be set to display.",
                        dataPath: "HideIcon"
                    },
                    {
                        type: SettingsType.Icon,
                        label: "Title Icon",
                        tooltip: "Sets the icon..",
                        dataPath: "TitleIcon",
                        isHidden: (d) => d.HideIcon
                    },
                    {
                        type: SettingsType.String,
                        label: 'Title',
                        tooltip: "Sets the title.",
                        dataPath: "Title",
                    },
                ]
            },
            {
                name: "Odometer",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: "Animation Count",
                        tooltip: "Whether to support or not animation.",
                        dataPath: "AnimationCount"
                    },
                    {
                        type: SettingsType.DropDown,
                        label: "Theme",
                        tooltip: "Selects the widget's theme. Available options (car, minimal, plaza, digital, slot-machine, train-station)",
                        dataPath: "Theme",
                        items: [
                            { label: "Car", value: "car" },
                            { label: "Minimal", value: "minimal" },
                            { label: "Plaza", value: "plaza" },
                            { label: "Digital", value: "digital" },
                            { label: "SlotMachine", value: "slot-machine" },
                            { label: "TrainStation", value: "train-station" }
                        ]
                    }
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
}
// =================================================================================================
export class OdometerWidget extends IWidgetThingsClass<OdometerData> {
    Render(key: string, width: number, height: number): ReactNode {
        return <Odometer />;
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <OdometerSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(): WebThingPort[] {
        let ports: WebThingPort[] = [];

        ports.push(new WebThingPort({
            Name: 'Value',
            PortKey: PortKeys.Value,
            Type: Plegma.ePortType.Decimal,
            ioDirection: Plegma.ioPortDirection.Input,
            State: ''
        }, WebThingPortType.Data));

        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        // Add thing's ports.
        webThing.Ports = {};
        this.createThingPorts().forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode.GetModule(this.WebModuleKey).UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts();
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): OdometerData {
        return {
            // TODO: Set the default values for the widget
            Theme: OdometerTheme.car,
            AnimationCount: true,
            Format: "(,ddd)",
            TextColor: "white",
            BackgroundColor: "#00bcd4",
            Title: "Total Stats",
            TitleIcon: "fa-star",
            HideIcon: false
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return OdometerWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // TODO: Update the widget Description
        return {
            Title: 'Odometer',
            WidgetTitle: '',
            Key: 'OdometerWidget',
            Type: 'OdometerWidget',
            Data: OdometerWidget.GetDefaultWidgetDataStatic(),
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/tile.svg'
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        const res = {};

        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        if (!IsNullOrEmpty(this.WidgetData.Title))
            res['Title'] = this.WidgetData.Title;

        return res;
    };
    // ---------------------------------------------------------------------------------------------
}